import { useCallback, useMemo } from "react";
import type { Participant } from "@/react/types/Live/Participant";
import type { Peer } from "@/react/types/Live/Peer";
import { useLiveStreamContextData } from "@circle-react/contexts/LiveStreams";
import { useProviderPeersList } from "./provider";

interface SortablePeer {
  customerUserId?: string | null;
  name?: string;
}

const sortAlphabetically =
  (participants: Record<number, Participant>) =>
  (participantA: SortablePeer, participantB: SortablePeer) => {
    const customerUserIdA =
      participantA?.customerUserId && parseInt(participantA.customerUserId);
    const customerUserIdB =
      participantB?.customerUserId && parseInt(participantB.customerUserId);
    const nameA = customerUserIdA
      ? (participants?.[customerUserIdA]?.name ?? "")
      : "";
    const nameB = customerUserIdB
      ? (participants?.[customerUserIdB]?.name ?? "")
      : "";
    return nameA.localeCompare(nameB);
  };

export const useActiveParticipantList = () => {
  const { hosts, spectators, moderators } = useProviderPeersList();
  const { room, participants } = useLiveStreamContextData();

  const isCreator = useCallback(
    (peer: Peer) =>
      participants?.[Number(peer.customerUserId || -1)]?.community_member_id ===
      room?.creator?.id,
    [participants, room?.creator?.id],
  );

  const sortByRole = useCallback(
    (hostA: Peer, hostB: Peer) => {
      if (isCreator(hostA)) return -1;
      if (isCreator(hostB)) return 1;
      return 0;
    },
    [isCreator],
  );

  const sortParticipants = useCallback(
    (peers: Peer[] | null) =>
      (peers && [...peers].sort(sortAlphabetically(participants))) || [],
    [participants],
  );

  const orderedHosts = useMemo(
    () => sortParticipants(hosts).sort(sortByRole),
    [hosts, sortParticipants, sortByRole],
  );

  const orderedModerators = useMemo(
    () => sortParticipants(moderators),
    [moderators, sortParticipants],
  );

  const orderedSpectators = useMemo(
    () => sortParticipants(spectators),
    [spectators, sortParticipants],
  );

  const host = useMemo(
    () => orderedHosts.find(isCreator),
    [orderedHosts, isCreator],
  );

  const cohosts = useMemo(
    () =>
      host
        ? orderedHosts.filter(
            peer => peer.customerUserId !== host.customerUserId,
          )
        : orderedHosts,
    [orderedHosts, host],
  );

  const peers = [...orderedHosts, ...orderedModerators, ...orderedSpectators];

  return {
    peers,
    hosts: orderedHosts,
    cohosts,
    moderators: host ? [host, ...orderedModerators] : orderedModerators,
    spectators: orderedSpectators,
    count: peers.length,
  };
};
